import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { ProductCatalogModal } from '../cart/ProductCatalogModal';
import { PrescriptionForm } from './PrescriptionForm';
import { LabTestForm, LabOrderDetails } from './LabTestForm';
import { NewPaymentMethodSelector, NewPaymentMethod, ShippingDestination } from './NewPaymentMethodSelector';
import { OrderReview } from './OrderReview';
import { TransferredRxPanel, TransferredRx, validateTransferredRx } from './TransferredRxPanel';
import { apiClient } from '../../lib/api';
import { Product, PrescriptionData, ProductWithPrescription } from '../../types/domain';
import { useUser } from '../../store/useAuthStore';

type WorkflowStep = 'catalog' | 'prescription' | 'payment' | 'review';

interface PrescriptionOrderWorkflowProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
  patientName: string;
  onOrderComplete: (orderId: string, opts?: { optimisticSubmitted?: boolean }) => void;
  initialDraftId?: string; // For editing existing drafts
}

export const PrescriptionOrderWorkflow: React.FC<PrescriptionOrderWorkflowProps> = ({
  isOpen,
  onClose,
  patientId,
  patientName,
  onOrderComplete,
  initialDraftId: _initialDraftId
}) => {
  const user = useUser();
  const [currentStep, setCurrentStep] = useState<WorkflowStep>('catalog');
  const [prescriptions, setPrescriptions] = useState<ProductWithPrescription[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<NewPaymentMethod>('physician_billed');
  const [editingPrescriptionId, setEditingPrescriptionId] = useState<string | null>(null);
  const [isWritingOrder, setIsWritingOrder] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [patientData, setPatientData] = useState<any>(null);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [shippingDestination, setShippingDestination] = useState<ShippingDestination>('patient');
  const [markupOverrides, setMarkupOverrides] = useState<Record<string, number>>({});
  // Per-order lab details (diagnosis codes, collection), keyed by productId —
  // the product's defaults are only the starting point.
  const [labDetails, setLabDetails] = useState<Record<string, LabOrderDetails>>({});
  const [markupDefaultsLoaded, setMarkupDefaultsLoaded] = useState(false);
  // Physician's saved sig presets (productId → sigs), shown first in the sig dropdown
  const [savedSigsMap, setSavedSigsMap] = useState<Record<string, string[]>>({});
  const [savedSigsLoaded, setSavedSigsLoaded] = useState(false);

  // Pharmacy accounts can attach an outside doctor's script to the order; every
  // other clinic type prescribes on its own credential and never sees the panel.
  const isPharmacy = (user?.physicianData as any)?.isPharmacy === true;
  const [transferredRx, setTransferredRx] = useState<TransferredRx | null>(null);

  // Get physician settings for payment options (controlled at physician level in DB)
  const patientPayEnabled = user?.physicianData?.patientPayEnabled || false;
  
  // Calculate total amount for all prescriptions (includes markup for patient_pay)
  const totalAmount = prescriptions.reduce((total, prescription) => {
    const productPrice = prescription.product.calculatedPrice?.finalPrice || prescription.product.basePrice || 0;
    const markup = paymentMethod === 'patient_pay' ? (markupOverrides[prescription.product._id] || 0) : 0;
    return total + ((productPrice + markup) * prescription.prescription.quantity);
  }, 0);

  // Auto-select patient pay if enabled (since it's recommended) - only on initial load
  React.useEffect(() => {
    if (patientPayEnabled && paymentMethod === 'physician_billed' && prescriptions.length === 0) {
      // Only auto-select when no prescriptions yet (initial state)
      setPaymentMethod('patient_pay');
    }
  }, [patientPayEnabled]); // Remove paymentMethod from dependencies to prevent override

  // Fetch patient data when component loads
  React.useEffect(() => {
    const fetchPatientData = async () => {
      if (!patientId || patientData) return;
      
      setLoadingPatient(true);
      try {
        const response = await apiClient.get(`/patients/${patientId}`);
        if (response.success && response.data) {
          setPatientData(response.data);
        }
      } catch (error) {
        console.error('Error fetching patient data:', error);
      } finally {
        setLoadingPatient(false);
      }
    };

    fetchPatientData();
  }, [patientId]);

  // Load the physician's saved sigs once per workflow open
  React.useEffect(() => {
    if (!isOpen || savedSigsLoaded) return;
    const loadSavedSigs = async () => {
      try {
        const res = await apiClient.getMyProductSigs();
        if (res.success && res.data?.sigs) {
          setSavedSigsMap(res.data.sigs);
        }
      } catch (error) {
        console.error('Error loading saved sigs:', error);
      }
      setSavedSigsLoaded(true);
    };
    loadSavedSigs();
  }, [isOpen, savedSigsLoaded]);

  // Inline "save this sig" from the prescription form — persist and update local cache
  const handleSaveSigDefault = async (productId: string, sig: string) => {
    try {
      const res = await apiClient.addMyProductSig(productId, sig);
      if (res.success && res.data?.sigs) {
        setSavedSigsMap(prev => ({ ...prev, [productId]: res.data!.sigs }));
      } else if (!res.success && res.error) {
        console.error('Failed to save sig default:', res.error);
      }
    } catch (error) {
      console.error('Error saving sig default:', error);
    }
  };

  // Load markup defaults when entering review step with patient_pay
  React.useEffect(() => {
    if (currentStep === 'review' && paymentMethod === 'patient_pay' && !markupDefaultsLoaded) {
      const loadDefaults = async () => {
        try {
          const res = await apiClient.getPatientPayMarkupDefaults();
          if (res.success && res.data?.defaults) {
            // Pre-fill markupOverrides with defaults for products in this order
            const defaults = res.data.defaults;
            const overrides: Record<string, number> = {};
            for (const p of prescriptions) {
              overrides[p.product._id] = defaults[p.product._id] || 0;
            }
            setMarkupOverrides(overrides);
          }
        } catch (error) {
          console.error('Error loading markup defaults:', error);
        }
        setMarkupDefaultsLoaded(true);
      };
      loadDefaults();
    }
  }, [currentStep, paymentMethod]);

  const handleWriteOrder = async () => {
    try {
      setIsWritingOrder(true);

      // Validate patient has email or phone for patient_pay orders
      if (paymentMethod === 'patient_pay') {
        const hasEmail = patientData?.email && patientData.email.trim() !== '';
        const hasPhone = patientData?.phone && patientData.phone.trim() !== '';

        if (!hasEmail && !hasPhone) {
          alert('❌ Cannot create patient pay order.\n\nThe patient must have an email address or phone number to receive payment links.\n\nPlease update the patient profile with contact information first.');
          setIsWritingOrder(false);
          return;
        }
      }

      // Transferred outside script (pharmacy accounts only) — block the order
      // rather than let an incomplete prescriber reach the pharmacy.
      if (transferredRx) {
        const transferError = validateTransferredRx(transferredRx);
        if (transferError) {
          alert(`❌ Cannot place this order.\n\n${transferError}`);
          setIsWritingOrder(false);
          return;
        }
      }

      // Prepare order data for API - map to backend format
      const orderData = {
        patientId,
        paymentMethod: paymentMethod,
        shippingDestination: shippingDestination,
        ...(transferredRx ? { prescriptionTransfer: transferredRx } : {}),
        items: prescriptions.map(p => ({
          productId: p.product._id,
          quantity: p.prescription.quantity,
          ...(paymentMethod === 'patient_pay' && markupOverrides[p.product._id] > 0
            ? { physicianMarkup: markupOverrides[p.product._id] }
            : {}),
          selectedVariants: {
            prescriptionData: JSON.stringify({
              sig: p.prescription.sig,
              pharmacyNotes: p.prescription.pharmacyNotes,
              daysSupply: p.prescription.daysSupply,
              paymentMethod: paymentMethod
            }),
            // Lab panels carry a requisition instead of a sig. Sent through the
            // same channel the prescription data already uses.
            ...(p.product.productType === 'lab' && labDetails[p.product._id]
              ? { labData: JSON.stringify(labDetails[p.product._id]) }
              : {})
          }
        }))
      };

      // Create order with appropriate status based on payment method
      const orderResponse = await apiClient.createOrder(orderData);

      if (orderResponse.success) {
        const orderId = orderResponse.data._id || orderResponse.data.orderId;

        // Finalize order by calling writeScript (works for both payment methods)
        const writeScriptResponse = await apiClient.writeScript(orderId);

        if (writeScriptResponse.success) {
          if (paymentMethod === 'patient_pay') {
            // Patient payment order - payment link ready/sent
            const linkMessage = writeScriptResponse.data?.paymentLinkSent
              ? '📱 A payment link has been sent to the patient.'
              : '📋 Payment link ready for patient.';
            alert(`✅ Order written successfully!\n\n${linkMessage}\n\nYou can manage payment links and track status from the Orders page.`);
          } else {
            // Physician billed order - invoice generated. If backend auto-charged
            // the saved card (MIT), call that out. If auto-charge was attempted
            // but failed, surface the reason so physician knows to pay manually.
            if (writeScriptResponse.data?.autoCharged) {
              alert(`✅ Order written successfully!\n\n💳 Card on file charged — order will submit to pharmacy once payment confirms.`);
            } else if (writeScriptResponse.data?.autoChargeError) {
              alert(`✅ Order written successfully!\n\n⚠️ Invoice created, but card-on-file charge failed: ${writeScriptResponse.data.autoChargeError}\n\nPlease pay the invoice manually or update your card on file.`);
            } else {
              alert(`✅ Order written successfully!\n\n📋 An invoice has been generated.\n\nYou can view and manage this order from the Orders page.`);
            }
          }
          // When the MIT auto-charge succeeded, the backend defers pharmacy
          // submission to the payment webhook, so a fresh fetch still shows the
          // order as "Awaiting Payment". Trust the webhook and tell the parent
          // to optimistically render it as submitted, restoring the snappy
          // behavior we had before the inline submit moved to the webhook.
          const optimisticSubmitted =
            paymentMethod === 'physician_billed' && !!writeScriptResponse.data?.autoCharged;
          onOrderComplete(orderId, { optimisticSubmitted });
          onClose();
          handleWorkflowClose();
        } else {
          throw new Error(writeScriptResponse.error || 'Failed to write script');
        }
      } else {
        throw new Error(orderResponse.error || 'Failed to create order');
      }
    } catch (error: any) {
      console.error('Error writing order:', error);
      const message = error?.message || 'Unknown error';
      alert(`Failed to place order: ${message}`);
    } finally {
      setIsWritingOrder(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);

      // A half-filled transfer would be rejected by the API, so surface it here
      // instead of failing the save.
      if (transferredRx) {
        const transferError = validateTransferredRx(transferredRx);
        if (transferError) {
          alert(`❌ Cannot save this draft.\n\n${transferError}`);
          setIsSavingDraft(false);
          return;
        }
      }

      // Prepare draft order data
      const orderData = {
        patientId,
        paymentMethod: paymentMethod,
        shippingDestination: shippingDestination,
        ...(transferredRx ? { prescriptionTransfer: transferredRx } : {}),
        items: prescriptions.map(p => ({
          productId: p.product._id,
          quantity: p.prescription.quantity,
          selectedVariants: {
            prescriptionData: JSON.stringify({
              sig: p.prescription.sig,
              pharmacyNotes: p.prescription.pharmacyNotes,
              daysSupply: p.prescription.daysSupply,
              paymentMethod: paymentMethod
            })
          }
        }))
      };

      const response = await apiClient.createOrder(orderData);
      
      if (response.success) {
        alert('Draft saved successfully!');
        onOrderComplete(response.data._id || response.data.orderId);
        onClose();
        handleWorkflowClose();
      } else {
        throw new Error(response.error || 'Failed to save draft');
      }
    } catch (error: any) {
      console.error('Error saving draft:', error);
      const message = error?.message || 'Unknown error';
      alert(`Failed to save draft: ${message}`);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleWorkflowClose = () => {
    // Reset all state
    setPrescriptions([]);
    setPaymentMethod('physician_billed');
    setShippingDestination('patient');
    setTransferredRx(null);
    setMarkupOverrides({});
    setMarkupDefaultsLoaded(false);
    setSavedSigsLoaded(false); // refetch on next open (may have changed on product page)
    setCurrentStep('catalog');
    setSelectedProduct(null);
    setEditingPrescriptionId(null);
    onClose();
  };

  const getStepTitle = (): string => {
    switch (currentStep) {
      case 'catalog':
        return 'Select Products';
      case 'prescription':
        return 'Prescription Details';
      case 'payment':
        return 'Payment Method';
      case 'review':
        return 'Review Order';
      default:
        return 'Create Order';
    }
  };

  const canProceedToNextStep = (): boolean => {
    switch (currentStep) {
      case 'catalog':
        return prescriptions.length > 0;
      case 'prescription':
        return true; // Always can proceed from prescription step
      case 'payment':
        return paymentMethod !== null;
      case 'review':
        return prescriptions.length > 0;
      default:
        return false;
    }
  };

  const getNextStep = (): WorkflowStep => {
    switch (currentStep) {
      case 'catalog':
        return 'prescription';
      case 'prescription':
        return 'payment';
      case 'payment':
        return 'review';
      default:
        return 'catalog';
    }
  };

  const getPreviousStep = (): WorkflowStep => {
    switch (currentStep) {
      case 'prescription':
        return 'catalog';
      case 'payment':
        return 'prescription';
      case 'review':
        return 'payment';
      default:
        return 'catalog';
    }
  };

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
    setCurrentStep('prescription');
  };

  const handleLabSave = (prescription: PrescriptionData, lab: LabOrderDetails) => {
    if (selectedProduct) {
      setLabDetails(prev => ({ ...prev, [selectedProduct._id]: lab }));
    }
    handlePrescriptionSave(prescription);
  };

  const handlePrescriptionSave = (prescription: PrescriptionData) => {
    if (editingPrescriptionId) {
      // Edit existing prescription
      setPrescriptions(prev => 
        prev.map(p => 
          p.product._id === editingPrescriptionId 
            ? { ...p, prescription } 
            : p
        )
      );
      setEditingPrescriptionId(null);
    } else {
      // Add new prescription
      if (selectedProduct) {
        setPrescriptions(prev => [...prev, { product: selectedProduct, prescription }]);
      }
    }
    setSelectedProduct(null);
    setCurrentStep('catalog');
  };

  const handlePrescriptionEdit = (productId: string) => {
    const existing = prescriptions.find(p => p.product._id === productId);
    if (existing) {
      setSelectedProduct(existing.product);
      setEditingPrescriptionId(productId);
      setCurrentStep('prescription');
    }
  };

  const handlePrescriptionRemove = (productId: string) => {
    setPrescriptions(prev => prev.filter(p => p.product._id !== productId));
  };

  const handleNextStep = () => {
    if (canProceedToNextStep()) {
      setCurrentStep(getNextStep());
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(getPreviousStep());
  };

  if (!isOpen) return null;

  const renderContent = () => {
    if (currentStep === 'catalog') {
      return (
        <ProductCatalogModal
          isOpen={true}
          onClose={handleWorkflowClose}
          patientName={patientName}
          onProductSelect={handleProductSelect}
          existingPrescriptions={prescriptions}
          onEditPrescription={handlePrescriptionEdit}
          onRemovePrescription={handlePrescriptionRemove}
          showContinueButton={true}
          onContinue={() => setCurrentStep('payment')}
        />
      );
    }

    return (
      <ModalBody
        title={getStepTitle()}
        patientName={patientName}
        onClose={handleWorkflowClose}
        currentStep={currentStep}
        onPreviousStep={handlePreviousStep}
        onNextStep={handleNextStep}
        canProceed={canProceedToNextStep()}
        onSaveDraft={handleSaveDraft}
        isSavingDraft={isSavingDraft}
        selectedProduct={selectedProduct}
      >
        {currentStep === 'prescription' && (
          selectedProduct ? (
            selectedProduct.productType === 'lab' ? (
            <LabTestForm
              product={selectedProduct}
              onSave={handleLabSave}
              onCancel={() => {
                setSelectedProduct(null);
                setEditingPrescriptionId(null);
                setCurrentStep('catalog');
              }}
              initialData={labDetails[selectedProduct._id]}
            />
            ) : (
            <PrescriptionForm
              product={selectedProduct}
              onSave={handlePrescriptionSave}
              onCancel={() => {
                setSelectedProduct(null);
                setEditingPrescriptionId(null);
                setCurrentStep('catalog');
              }}
              initialData={editingPrescriptionId
                ? prescriptions.find(p => p.product._id === editingPrescriptionId)?.prescription
                : undefined
              }
              asModal={false}
              savedSigs={savedSigsMap[selectedProduct._id] || []}
              onSaveSigDefault={(sig) => handleSaveSigDefault(selectedProduct._id, sig)}
            />
            )
          ) : (
            <div className="text-center py-8">
              <div className="text-gray-500">
                <svg className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-medium">No product selected</p>
                <p className="text-sm">Please go back to select a product first.</p>
              </div>
            </div>
          )
        )}
        {currentStep === 'payment' && (
          <NewPaymentMethodSelector
            selectedMethod={paymentMethod}
            onMethodChange={(method) => {
              setPaymentMethod(method);
              // Reset to patient shipping if switching to patient_pay (clinic not allowed)
              if (method === 'patient_pay') {
                setShippingDestination('patient');
              }
            }}
            shippingDestination={shippingDestination}
            onShippingDestinationChange={setShippingDestination}
            patientName={patientName}
            clinicName={user?.profile?.firstName && user?.profile?.lastName
              ? `Dr. ${user.profile.firstName} ${user.profile.lastName}`
              : 'Your Clinic'
            }
            clinicAddress={user?.physicianData?.clinicAddress}
            patientPayEnabled={patientPayEnabled}
            totalAmount={totalAmount}
            patientAddress={patientData?.address}
            loadingPatient={loadingPatient}
          />
        )}
        {currentStep === 'review' && isPharmacy && (
          <div className="mb-4">
            <TransferredRxPanel value={transferredRx} onChange={setTransferredRx} />
          </div>
        )}
        {currentStep === 'review' && (
          <OrderReview
            patientName={patientName}
            prescriptions={prescriptions}
            paymentMethod={paymentMethod}
            onEditPrescription={handlePrescriptionEdit}
            onRemovePrescription={handlePrescriptionRemove}
            onWriteOrder={handleWriteOrder}
            isWriting={isWritingOrder}
            markupOverrides={paymentMethod === 'patient_pay' ? markupOverrides : undefined}
            onMarkupChange={paymentMethod === 'patient_pay' ? (productId, amount) => {
              setMarkupOverrides(prev => ({ ...prev, [productId]: amount }));
            } : undefined}
          />
        )}
      </ModalBody>
    );
  };

  return <>{renderContent()}</>;
};

// New ModalBody component
interface ModalBodyProps {
  title: string;
  patientName: string;
  onClose: () => void;
  currentStep: WorkflowStep;
  onPreviousStep: () => void;
  onNextStep: () => void;
  canProceed: boolean;
  children: React.ReactNode;
  onSaveDraft: () => void;
  isSavingDraft: boolean;
  selectedProduct: Product | null;
}

const ModalBody: React.FC<ModalBodyProps> = ({
  title,
  patientName,
  onClose,
  currentStep,
  onPreviousStep,
  onNextStep,
  canProceed,
  children,
  onSaveDraft,
  isSavingDraft,
  selectedProduct,
}) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          <p className="text-gray-600">Patient: {patientName}</p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100"
        >
          <span className="sr-only">Close</span>
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
        {(['catalog', 'prescription', 'payment', 'review'] as WorkflowStep[]).map((step, index) => (
          <div key={step} className="flex items-center">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              currentStep === step
                ? 'bg-blue-600 text-white'
                : ['catalog', 'prescription', 'payment', 'review'].indexOf(step) < ['catalog', 'prescription', 'payment', 'review'].indexOf(currentStep)
                ? 'bg-green-600 text-white'
                : 'bg-gray-300 text-gray-600'
            }`}>
              {index + 1}
            </div>
            <span className={`ml-2 text-sm font-medium ${
              currentStep === step ? 'text-blue-600' : 'text-gray-500'
            }`}>
              {step.charAt(0).toUpperCase() + step.slice(1)}
            </span>
            {index < 3 && (
              <div className={`flex-1 h-0.5 mx-4 ${
                ['catalog', 'prescription', 'payment', 'review'].indexOf(step) < ['catalog', 'prescription', 'payment', 'review'].indexOf(currentStep)
                  ? 'bg-green-600'
                  : 'bg-gray-300'
              }`} />
            )}
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {children}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
        <div>
          {currentStep !== 'catalog' && (
            <button
              onClick={onPreviousStep}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium rounded-md"
            >
              Previous
            </button>
          )}
        </div>
        <div className="flex items-center space-x-3">
          {currentStep === 'review' && (
            <button
              onClick={onSaveDraft}
              disabled={isSavingDraft}
              className={`px-4 py-2 font-medium rounded-md transition-colors ${
                isSavingDraft
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-600 text-white hover:bg-gray-700'
              }`}
            >
              {isSavingDraft ? 'Saving...' : 'Save Draft'}
            </button>
          )}

          {currentStep === 'prescription' && selectedProduct && (
            <button
              type="submit"
              form="prescription-form"
              className="px-6 py-2 font-medium rounded-md transition-colors bg-blue-600 text-white hover:bg-blue-700 flex items-center"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Save Prescription
            </button>
          )}
          
          {currentStep !== 'catalog' && currentStep !== 'review' && currentStep !== 'prescription' && (
            <button
              onClick={onNextStep}
              disabled={!canProceed}
              className={`px-6 py-2 font-medium rounded-md transition-colors ${
                canProceed
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Next
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
); 